import { Image as ImageIcon, Upload, X } from "lucide-react";
import { GradientButton } from "./GradientButton";

type UploadedFilePreviewProps = {
  selectedFile: File;
  previewUrl: string | null;
  isUploading: boolean;
  onClear: () => void;
  onUpload: () => void;
};

export const UploadedFilePreview = ({
  selectedFile,
  previewUrl,
  isUploading,
  onClear,
  onUpload
}: UploadedFilePreviewProps) => (
  <div
    className="relative rounded transition-all duration-300"
    style={{
      border: "1px solid transparent",
      background:
        "linear-gradient(#fff 0 0) padding-box, linear-gradient(164.9deg, #BA8DFF 4.95%, #F442F3 46.32%, #344CFF 86%) border-box"
    }}
    data-testid="uploaded-file-preview"
  >
    <div className="p-4">
      {/* File info header */}
      <div className="mb-4 flex items-center justify-between" data-testid="file-info-header">
        <div className="flex items-center space-x-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100">
            <ImageIcon className="h-4 w-4 text-gray-400" />
          </div>
          <div data-testid="file-details">
            <p className="text-sm font-semibold text-gray-700" data-testid="file-name">
              {selectedFile.name}
            </p>
            <p className="text-xs text-gray-500" data-testid="file-size">
              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
        </div>
        <button
          onClick={onClear}
          className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          aria-label="Remove file"
          data-testid="clear-file-button"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Image preview */}
      {previewUrl && (
        <div className="mb-4 flex justify-center" data-testid="image-preview-container">
          <div
            className="relative overflow-hidden rounded-lg"
            style={{
              border: "1px solid transparent",
              background:
                "linear-gradient(#fff, #fff) padding-box, linear-gradient(164.9deg, #BA8DFF 4.95%, #F442F3 46.32%, #344CFF 86%) border-box"
            }}
          >
            <img
              src={previewUrl}
              alt="Preview"
              className="h-20 w-20 object-cover"
              data-testid="preview-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent" />
          </div>
        </div>
      )}

      {/* Upload button */}
      <GradientButton
        onClick={onUpload}
        disabled={isUploading}
        className="w-full py-2"
        style={isUploading ? {} : { color: "white" }}
        data-testid="upload-button"
      >
        {isUploading ? (
          <>
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            <span className="ml-2">Uploading...</span>
          </>
        ) : (
          <>
            <Upload className="h-4 w-4" />
            <span className="ml-2">Upload & Start Refining</span>
          </>
        )}
      </GradientButton>
    </div>
  </div>
);
